import { NotificationSoundKey } from "@core/constants/notificationSound"

const DB_NAME = "sigma-client-local-media"
const DB_VERSION = 1
const STORE_NAME = "notification-sounds"

interface StoredNotificationSound {
  id: string
  blob: Blob
  fileName: string
  mimeType: string
  updatedAt: number
}

const getDatabase = async (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "id" })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error("IndexedDB 열기 실패"))
  })
}

const withStore = async <T>(
  mode: IDBTransactionMode,
  executor: (store: IDBObjectStore, resolve: (value: T) => void, reject: (reason?: unknown) => void) => void
) => {
  const database = await getDatabase()

  return new Promise<T>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, mode)
    const store = transaction.objectStore(STORE_NAME)

    transaction.onabort = () => reject(transaction.error || new Error("IndexedDB 트랜잭션 실패"))
    transaction.onerror = () => reject(transaction.error || new Error("IndexedDB 작업 실패"))
    transaction.oncomplete = () => database.close()

    executor(store, resolve, reject)
  })
}

const getNotificationSoundId = (soundKey: NotificationSoundKey, memberUuid?: string | null) => `${soundKey}:${memberUuid || "guest"}`

export const saveNotificationCustomSoundFile = async (
  soundKey: NotificationSoundKey,
  file: File,
  memberUuid?: string | null
): Promise<{ fileName: string; updatedAt: number }> => {
  const record: StoredNotificationSound = {
    id: getNotificationSoundId(soundKey, memberUuid),
    blob: file,
    fileName: file.name,
    mimeType: file.type || "audio/*",
    updatedAt: Date.now(),
  }

  await withStore<void>("readwrite", (store, resolve, reject) => {
    const request = store.put(record)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error || new Error("커스텀 알림음 저장 실패"))
  })

  return { fileName: record.fileName, updatedAt: record.updatedAt }
}

export const getNotificationCustomSoundBlob = async (soundKey: NotificationSoundKey, memberUuid?: string | null): Promise<Blob | null> => {
  return withStore<Blob | null>("readonly", (store, resolve, reject) => {
    const request = store.get(getNotificationSoundId(soundKey, memberUuid))
    request.onsuccess = () => {
      const record = request.result as StoredNotificationSound | undefined
      resolve(record?.blob || null)
    }
    request.onerror = () => reject(request.error || new Error("커스텀 알림음 조회 실패"))
  })
}

export const deleteNotificationCustomSoundFile = async (soundKey: NotificationSoundKey, memberUuid?: string | null): Promise<void> => {
  await withStore<void>("readwrite", (store, resolve, reject) => {
    const request = store.delete(getNotificationSoundId(soundKey, memberUuid))
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error || new Error("커스텀 알림음 삭제 실패"))
  })
}

export const saveTreatmentEndCustomSoundFile = async (file: File, memberUuid?: string | null): Promise<{ fileName: string; updatedAt: number }> => {
  return saveNotificationCustomSoundFile("treatmentEnd", file, memberUuid)
}

export const getTreatmentEndCustomSoundBlob = async (memberUuid?: string | null): Promise<Blob | null> => {
  return getNotificationCustomSoundBlob("treatmentEnd", memberUuid)
}

export const deleteTreatmentEndCustomSoundFile = async (memberUuid?: string | null): Promise<void> => {
  return deleteNotificationCustomSoundFile("treatmentEnd", memberUuid)
}

